import React, { useMemo } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

export function cx(...a) {
  return a.filter(Boolean).join(' ');
}

export const formatEuro = (n) => {
  const num = Number(n) || 0;
  const fractionDigits = num % 1 === 0 ? 0 : 2;
  return `€${num.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
};

export function StatusPill({ status }) {
  const { t } = useTranslation();
  const map = {
    Pending: { bg: 'bg-amber-50', fg: 'text-amber-700', dot: 'bg-amber-500' },
    Approved: { bg: 'bg-blue-50', fg: 'text-blue-700', dot: 'bg-blue-500' },
    Paid: {
      bg: 'bg-emerald-50',
      fg: 'text-emerald-700',
      dot: 'bg-emerald-500',
    },
    Confirmed: {
      bg: 'bg-emerald-50',
      fg: 'text-emerald-700',
      dot: 'bg-emerald-500',
    },
    Rejected: { bg: 'bg-rose-50', fg: 'text-rose-700', dot: 'bg-rose-500' },
    Cancelled: {
      bg: 'bg-slate-100',
      fg: 'text-slate-600',
      dot: 'bg-slate-400',
    },
    Expired: { bg: 'bg-gray-100', fg: 'text-gray-600', dot: 'bg-gray-400' },
    Open: { bg: 'bg-gray-50', fg: 'text-gray-500', dot: 'bg-gray-300' },
  };
  const m = map[status] || map.Open;
  const label = t(`dashboard.em.status_pill.${status}`, status);
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold',
        m.bg,
        m.fg
      )}
    >
      <span className={cx('w-1.5 h-1.5 rounded-full', m.dot)} />
      {label}
    </span>
  );
}

// Where the money ended up on a cancelled booking. 'Refunded' is the resolved
// case (admin issued it by hand); 'Paid' means the booking was cancelled with
// the money still taken, which needs someone to look at it — hence amber.
export function PaymentPill({ status }) {
  const { t } = useTranslation();
  const map = {
    Refunded: {
      bg: 'bg-indigo-50',
      fg: 'text-indigo-700',
      dot: 'bg-indigo-500',
    },
    Paid: { bg: 'bg-amber-50', fg: 'text-amber-700', dot: 'bg-amber-500' },
  };
  const m = map[status];
  if (!m) return null;
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold',
        m.bg,
        m.fg
      )}
    >
      <span className={cx('w-1.5 h-1.5 rounded-full', m.dot)} />
      {t(`dashboard.em.payment_pill.${status}`, status)}
    </span>
  );
}

export function Kpi({ label, value, sub, trend }) {
  return (
    <div className="flex-1 min-w-0 rounded-xl bg-white/75 backdrop-blur-sm px-3 py-2.5 ring-1 ring-black/5">
      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide truncate">
        {label}
      </p>
      <div className="flex items-baseline gap-1.5 mt-0.5 min-w-0">
        <p className="text-base sm:text-lg font-bold text-gray-900 tabular-nums truncate">
          {value}
        </p>
        {trend && (
          <span className="text-[10px] font-semibold text-emerald-600 shrink-0">
            {trend}
          </span>
        )}
      </div>
      {sub && (
        <p className="text-[10px] text-gray-500 mt-0.5 truncate">{sub}</p>
      )}
    </div>
  );
}

function getEventWindow(dateObj) {
  if (!dateObj || !dateObj.type)
    return { start: null, end: null, openHours: null, closeHours: null };
  if (dateObj.type === 'fixed') {
    const start = moment(dateObj.startDate, 'YYYY-MM-DD');
    const end = moment(dateObj.endDate, 'YYYY-MM-DD');
    return {
      start: start.isValid() ? start : null,
      end: end.isValid() ? end : null,
      openHours: dateObj.openHours,
      closeHours: dateObj.closeHours,
    };
  }
  if (
    dateObj.type === 'segmented' &&
    Array.isArray(dateObj.days) &&
    dateObj.days.length
  ) {
    const sorted = [...dateObj.days]
      .filter((d) => d?.date && moment(d.date, 'YYYY-MM-DD').isValid())
      .sort((a, b) => a.date.localeCompare(b.date));
    if (!sorted.length)
      return { start: null, end: null, openHours: null, closeHours: null };
    return {
      start: moment(sorted[0].date, 'YYYY-MM-DD'),
      end: moment(sorted[sorted.length - 1].date, 'YYYY-MM-DD'),
      openHours: sorted[0].openHours,
      closeHours: sorted[sorted.length - 1].closeHours,
    };
  }
  return { start: null, end: null, openHours: null, closeHours: null };
}

export function EventHeaderCard({
  event,
  isPromoter,
  onPreview,
  onShare,
  onDuplicate,
  onArchive,
}) {
  const { t } = useTranslation();

  const { start, end, openHours, closeHours } = useMemo(
    () => getEventWindow(event?.date),
    [event?.date]
  );

  const dateLabel = useMemo(() => {
    if (!start || !end) return '';
    const fmt = (d) => d.format('DD MMM');
    const range = start.isSame(end, 'day')
      ? fmt(start)
      : `${fmt(start)} – ${fmt(end)}`;
    const hours =
      openHours && closeHours ? ` · ${openHours}–${closeHours}` : '';
    return `${range}${hours}`;
  }, [start, end, openHours, closeHours]);

  // Schedule badge: upcoming → start date + days until start; live → end
  // date + days remaining; over → end date.
  const scheduleBadge = useMemo(() => {
    if (!start || !end) return null;
    const today = moment().startOf('day');
    const fmt = (d) => d.format('DD MMM');
    if (today.isBefore(start, 'day')) {
      return {
        state: 'upcoming',
        label: t('dashboard.em.starts_in_days', {
          date: fmt(start),
          count: start.diff(today, 'days'),
        }),
      };
    }
    if (today.isSameOrBefore(end, 'day')) {
      return {
        state: 'live',
        label: t('dashboard.em.live_days_left', {
          date: fmt(end),
          count: end.diff(today, 'days') + 1,
        }),
      };
    }
    return {
      state: 'ended',
      label: t('dashboard.em.ended_on', { date: fmt(end) }),
    };
  }, [start, end, t]);

  const icon = event?.avatar || null;
  const category =
    event?.category ||
    (Array.isArray(event?.categoriesAllowed) &&
      event.categoriesAllowed[0]?.name) ||
    null;
  const address = event?.formattedAddress || event?.location || '';

  return (
    <div
      className="relative rounded-2xl overflow-hidden ring-1 ring-black/5"
      style={{ background: 'linear-gradient(135deg,#F0A830 0%,#7C3AED 100%)' }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/30 to-black/10" />
      <div className="relative flex items-center gap-3 p-3 sm:p-3.5 pr-12">
        <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-white ring-2 ring-white/80 shadow-md overflow-hidden shrink-0">
          {icon ? (
            <img
              src={icon}
              alt={event?.name ? `${event.name} logo` : ''}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div
              className="w-full h-full"
              style={{
                background: 'linear-gradient(135deg,#F0A830 0%,#7C3AED 100%)',
              }}
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-white text-base sm:text-lg font-bold leading-tight truncate">
              {event?.name}
            </h2>
            {scheduleBadge && (
              <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/95 text-[10px] font-semibold text-gray-800 whitespace-nowrap shrink-0">
                <span
                  className={cx(
                    'w-1.5 h-1.5 rounded-full',
                    scheduleBadge.state === 'live' &&
                      'bg-emerald-500 em-softpulse',
                    scheduleBadge.state === 'upcoming' && 'bg-amber-500',
                    scheduleBadge.state === 'ended' && 'bg-gray-400'
                  )}
                />
                {scheduleBadge.label}
              </span>
            )}
            {category && (
              <span className="hidden lg:inline-flex items-center px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm text-[10px] font-medium text-white ring-1 ring-white/30 whitespace-nowrap shrink-0">
                {category}
              </span>
            )}
          </div>
          <p className="text-white/85 text-[11px] sm:text-xs mt-0.5 truncate">
            {[address, dateLabel].filter(Boolean).join(' · ')}
          </p>
        </div>
        {isPromoter && (
          <div className="hidden sm:flex items-center gap-2 shrink-0 self-end">
            {onShare && (
              <button
                type="button"
                onClick={onShare}
                className="px-3 py-1.5 rounded-lg bg-white/15 backdrop-blur-sm text-white text-xs font-semibold ring-1 ring-white/30 hover:bg-white/25 transition"
              >
                {t('dashboard.em.share')}
              </button>
            )}
            <button
              type="button"
              onClick={onPreview}
              className="px-3 py-1.5 rounded-lg bg-white text-gray-900 text-xs font-semibold hover:bg-gray-100 transition"
            >
              {t('dashboard.em.view_page')} ↗
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function TabBar({ tabs, activeIndex, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {tabs.map((tab, i) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onChange(i)}
          className={cx(
            'px-4 py-2 text-sm font-bold rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
            activeIndex === i
              ? 'bg-brand text-white shadow-sm'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          )}
        >
          <span className="flex items-center gap-2">
            {tab.label}
            {tab.count != null && (
              <span
                className={cx(
                  'px-1.5 py-0.5 rounded-full text-[10px]',
                  activeIndex === i
                    ? 'bg-white/25 text-white'
                    : 'bg-white text-gray-600'
                )}
              >
                {tab.count}
              </span>
            )}
          </span>
        </button>
      ))}
    </div>
  );
}

export function CloseBtn({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label || 'Close'}
      className="absolute top-5 right-5 [@media(max-height:700px)]:top-[18px] [@media(max-height:700px)]:right-[18px] w-9 h-9 rounded-full bg-white/80 backdrop-blur ring-1 ring-black/10 text-gray-700 hover:bg-white hover:text-gray-900 hover:scale-105 active:scale-95 transition flex items-center justify-center z-10"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M3 3l10 10M13 3L3 13"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}

export function QuickAction({ icon, label, onClick, danger }) {
  const icons = {
    eye: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
    dup: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <rect x="9" y="9" width="13" height="13" rx="2" />
        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
      </svg>
    ),
    arch: (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <rect x="2" y="3" width="20" height="5" rx="1" />
        <path d="M4 8v11a2 2 0 002 2h12a2 2 0 002-2V8M10 12h4" />
      </svg>
    ),
  };
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={cx(
        'inline-flex items-center justify-center w-8 h-8 rounded-lg transition ring-1 shrink-0',
        danger
          ? 'text-rose-600 hover:bg-rose-50 ring-rose-100'
          : 'text-gray-600 hover:bg-gray-100 ring-gray-200'
      )}
    >
      {icons[icon]}
    </button>
  );
}
